import React, { useState, useRef, useCallback } from 'react';
import { LabelDefinition } from '../types';
import { renderMarkdown } from '../utils/markdown';

interface LabelTooltipProps {
  label: string;
  definition?: LabelDefinition;
  /** The trigger element. The tooltip wraps this and shows on hover/focus. */
  children: React.ReactNode;
}

/**
 * BQ-18: Lightweight hover/focus tooltip showing the per-label annotation
 * guidance (description + examples + counter-examples). When the definition
 * is undefined OR all of its fields are empty, this component is a no-op
 * pass-through — callers always get the trigger rendered, only the popover
 * is conditional.
 *
 * Implementation notes:
 *  - No external library: a plain Tailwind-styled absolutely-positioned div.
 *  - Hover open uses `onMouseEnter` / `onMouseLeave`. Keyboard focus on the
 *    trigger also opens it (via `onFocus` / `onBlur`) so this is reachable
 *    without a pointer.
 *  - We cap the visible examples to the first 3 so the popover stays
 *    compact; the editor (LabelEditor) is the canonical place to see/edit
 *    the full lists.
 */
const LabelTooltip: React.FC<LabelTooltipProps> = ({
  label,
  definition,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  const hasDescription = !!definition?.description?.trim();
  const examples = (definition?.examples ?? []).filter(
    (s) => typeof s === 'string' && s.trim().length > 0
  );
  const counterExamples = (definition?.counterExamples ?? []).filter(
    (s) => typeof s === 'string' && s.trim().length > 0
  );
  const hasAnyContent =
    hasDescription || examples.length > 0 || counterExamples.length > 0;

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  // No definition (or all fields empty) → render children unchanged. Keeps
  // the wrapper truly free of hover/focus side effects in the empty case.
  if (!definition || !hasAnyContent) {
    return <>{children}</>;
  }

  return (
    <span
      ref={wrapperRef}
      className="relative inline-block"
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      onFocus={handleOpen}
      onBlur={handleClose}
    >
      {children}
      {isOpen && (
        <span
          role="tooltip"
          className="absolute z-50 left-0 top-full mt-1 w-72 max-w-[18rem] bg-white text-gray-800 text-xs rounded-md shadow-lg border border-gray-200 p-3 normal-case font-normal"
          // Inline style guards against odd parent text-* classes leaking in.
          style={{ pointerEvents: 'none' }}
        >
          <div className="font-semibold text-gray-900 mb-1 break-words">
            {label}
          </div>
          {hasDescription && (
            <div
              className="prose prose-xs max-w-none text-gray-700 mb-2"
              // The HTML here is sanitized via DOMPurify in renderMarkdown.
              dangerouslySetInnerHTML={{
                __html: renderMarkdown(definition!.description!),
              }}
            />
          )}
          {examples.length > 0 && (
            <div className="mb-1.5">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">
                Examples
              </div>
              <ul className="space-y-0.5">
                {examples.slice(0, 3).map((ex, i) => (
                  <li
                    key={`ex-${i}`}
                    className="flex items-start gap-1 text-green-700"
                  >
                    <span aria-hidden="true">✓</span>
                    <span className="break-words">{ex}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {counterExamples.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">
                Counter-examples
              </div>
              <ul className="space-y-0.5">
                {counterExamples.slice(0, 3).map((ex, i) => (
                  <li
                    key={`cex-${i}`}
                    className="flex items-start gap-1 text-red-700"
                  >
                    <span aria-hidden="true">✗</span>
                    <span className="break-words">{ex}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </span>
      )}
    </span>
  );
};

export default LabelTooltip;
